import { useEffect, useState, useCallback } from "react";
import api from "@/lib/apiClient";
import { MODULES } from "@/lib/modules";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ShieldCheck, Check, Trash2, Clock, Settings2, UserCog, UserPlus, Copy, Link as LinkIcon, Tags, Plus, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const APPROVER_ROLES = ["coordenador", "coordenador_geral", "pastor_senior"];
const STRONG_ROLES = ["coordenador", "coordenador_geral", "pastor_senior", "supervisor", "lider"];

function CreateUserDialog({ roles, onCreated, isApprover }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("membro");
  const options = roles.filter((r) => isApprover || !STRONG_ROLES.includes(r.key));

  const submit = async () => {
    if (!name.trim() || !email.trim() || !password.trim()) return toast.error("Preencha nome, e-mail e senha");
    try {
      await api.post("/users", { name, email, password, role });
      toast.success("Usuário criado!");
      setOpen(false); setName(""); setEmail(""); setPassword(""); setRole("membro");
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao criar usuário"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2" data-testid="create-user-button"><UserPlus className="h-4 w-4" /> Novo usuário</Button>
      </DialogTrigger>
      <DialogContent className="max-h-[88vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Novo usuário</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Nome completo" value={name} onChange={(e) => setName(e.target.value)} data-testid="create-user-name" />
          <Input type="email" placeholder="E-mail" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="create-user-email" />
          <Input type="password" placeholder="Senha" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="create-user-password" />
          <div>
            <div className="text-xs text-muted-foreground mb-1.5">Tipo de cadastro</div>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger data-testid="create-user-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                {options.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {isApprover && (
              <p className="text-xs text-muted-foreground mt-1.5">Pastor Sênior, Coordenador Geral e Supervisor entram já aprovados quando criados aqui.</p>
            )}
          </div>
          <Button onClick={submit} className="gap-2" data-testid="save-user-button"><Check className="h-4 w-4" /> Criar usuário</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RoleForm({ initial, onDone, trigger }) {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState(initial?.label || "");
  const [mods, setMods] = useState(initial?.modules || []);
  const [pub, setPub] = useState(initial ? initial.public : true);
  const [approval, setApproval] = useState(initial ? initial.requires_approval : false);
  const [admin, setAdmin] = useState(initial ? initial.is_admin : false);
  const [color, setColor] = useState(initial?.color || "#7C3AED");
  const COLORS = ["#EA580C", "#DC2626", "#7C3AED", "#0EA5E9", "#059669", "#D97706", "#64748B", "#F59E0B", "#DB2777", "#0D9488"];
  const isEdit = !!initial;

  const submit = async () => {
    if (!label.trim()) return toast.error("Informe o nome do tipo");
    const payload = { label, modules: mods, is_admin: admin, requires_approval: approval, public: pub, color };
    try {
      if (isEdit) await api.put(`/roles/${initial.key}`, payload);
      else await api.post("/roles", payload);
      toast.success(isEdit ? "Tipo atualizado!" : "Tipo criado!");
      setOpen(false);
      if (!isEdit) { setLabel(""); setMods([]); setPub(true); setApproval(false); setAdmin(false); }
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o && initial) { setLabel(initial.label); setMods(initial.modules); setPub(initial.public); setApproval(initial.requires_approval); setAdmin(initial.is_admin); setColor(initial.color || "#7C3AED"); } }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-h-[88vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{isEdit ? `Editar tipo · ${initial.label}` : "Novo tipo de cadastro"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Nome do tipo (ex: Diácono)" value={label} onChange={(e) => setLabel(e.target.value)} data-testid="new-role-input" />
          <div>
            <div className="text-xs text-muted-foreground mb-1.5">Cor</div>
            <div className="flex flex-wrap gap-2">
              {COLORS.map((c) => (
                <button key={c} type="button" onClick={() => setColor(c)}
                  className={`h-7 w-7 rounded-full border-2 ${color === c ? "border-foreground" : "border-transparent"}`}
                  style={{ background: c }} data-testid={`role-color-${c}`} />
              ))}
            </div>
          </div>
          <div className="text-xs text-muted-foreground">Módulos padrão:</div>
          <ModulePicker value={mods} onChange={setMods} />
          <div className="flex flex-wrap gap-4 text-sm pt-1">
            <label className="flex items-center gap-2"><Switch checked={pub} onCheckedChange={setPub} data-testid="role-public-switch" /> Público</label>
            <label className="flex items-center gap-2"><Switch checked={approval} onCheckedChange={setApproval} data-testid="role-approval-switch" /> Requer aprovação</label>
            <label className="flex items-center gap-2"><Switch checked={admin} onCheckedChange={setAdmin} disabled={initial?.system} data-testid="role-admin-switch" /> Admin</label>
          </div>
          <Button onClick={submit} className="gap-2" data-testid="save-role-button"><Check className="h-4 w-4" /> {isEdit ? "Salvar alterações" : "Criar tipo"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RolesManager({ roles, onChanged }) {
  const [open, setOpen] = useState(false);
  const remove = async (r) => {
    try { await api.delete(`/roles/${r.key}`); toast.success("Tipo removido"); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2" data-testid="manage-roles-button"><Tags className="h-4 w-4" /> Tipos de Cadastro</Button>
      </DialogTrigger>
      <DialogContent className="max-h-[88vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Tipos de Cadastro</DialogTitle></DialogHeader>
        <div className="space-y-2">
          {roles.map((r) => (
            <div key={r.key} className="flex items-center justify-between rounded-lg border border-border px-3 py-2" data-testid="role-row">
              <div className="text-sm flex items-center gap-2">
                <span className="h-3 w-3 rounded-full" style={{ background: r.color }} />
                <span className="font-medium">{r.label}</span>
                <span className="text-xs text-muted-foreground">
                  {r.is_admin ? "Admin · " : ""}{r.public ? "Público" : "Só admin"}{r.requires_approval ? " · aprovação" : ""} · {r.modules.length} mód.
                </span>
              </div>
              <div className="flex items-center gap-1">
                <RoleForm initial={r} onDone={onChanged}
                  trigger={<Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`edit-role-${r.key}`}><Settings2 className="h-4 w-4" /></Button>} />
                {!r.system && (
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => remove(r)} data-testid={`delete-role-${r.key}`}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="border-t border-border pt-4 mt-2">
          <RoleForm onDone={onChanged}
            trigger={<Button className="gap-2 w-full" data-testid="add-role-button"><Plus className="h-4 w-4" /> Novo tipo</Button>} />
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RoleSelect({ user, roles, onChanged }) {
  const change = async (role) => {
    if (role === user.role) return;
    try { await api.put(`/users/${user.id}/role`, { role }); toast.success("Tipo atualizado"); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  return (
    <Select value={user.role} onValueChange={change}>
      <SelectTrigger className="h-8 text-xs" data-testid={`role-select-${user.id}`}><SelectValue /></SelectTrigger>
      <SelectContent>
        {roles.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

const ROLE_LABEL = { coordenador: "Coordenador", lider: "Líder", membro: "Membro", visitante: "Visitante" };
const ROLE_CLS = {
  coordenador: "bg-primary/15 text-primary",
  lider: "bg-[hsl(var(--sky))]/15 text-[hsl(var(--sky))]",
  membro: "bg-secondary text-secondary-foreground",
  visitante: "bg-accent/15 text-accent",
};

function ModulePicker({ value, onChange }) {
  const toggle = (k) => onChange(value.includes(k) ? value.filter((x) => x !== k) : [...value, k]);
  return (
    <div className="grid grid-cols-2 gap-2">
      {MODULES.map((m) => (
        <label key={m.key} className={`flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer text-sm ${value.includes(m.key) ? "border-primary bg-primary/5" : "border-border"}`}>
          <Checkbox checked={value.includes(m.key)} onCheckedChange={() => toggle(m.key)} data-testid={`module-checkbox-${m.key}`} />
          {m.label}
        </label>
      ))}
    </div>
  );
}

function ApproveDialog({ user, onDone }) {
  const [open, setOpen] = useState(false);
  const [mods, setMods] = useState(["dashboard", "grupos", "frequencia", "aniversariantes", "oracoes", "calendario"]);
  const approve = async () => {
    try {
      await api.post(`/users/${user.id}/approve`, { allowed_modules: mods });
      toast.success(`${user.name} aprovado!`);
      setOpen(false);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao aprovar"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="gap-1.5" data-testid={`approve-user-${user.id}`}><Check className="h-4 w-4" /> Aprovar</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Aprovar {user.name}</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Selecione os módulos que esta pessoa poderá acessar:</p>
        <div className="py-2"><ModulePicker value={mods} onChange={setMods} /></div>
        <DialogFooter><Button onClick={approve} data-testid="confirm-approve-button">Aprovar e liberar acesso</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ModulesDialog({ user, onDone }) {
  const [open, setOpen] = useState(false);
  const [mods, setMods] = useState(user.allowed_modules || []);
  const save = async () => {
    await api.put(`/users/${user.id}/modules`, { allowed_modules: mods });
    toast.success("Acessos atualizados");
    setOpen(false);
    onDone();
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setMods(user.allowed_modules || []); }}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="gap-1.5" data-testid={`edit-modules-${user.id}`}><Settings2 className="h-4 w-4" /> Módulos</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Acessos de {user.name}</DialogTitle></DialogHeader>
        <div className="py-2"><ModulePicker value={mods} onChange={setMods} /></div>
        <DialogFooter><Button onClick={save} data-testid="save-modules-button">Salvar acessos</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InviteDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [mods, setMods] = useState(["dashboard", "grupos", "frequencia", "aniversariantes", "oracoes", "calendario"]);
  const [link, setLink] = useState("");

  const create = async () => {
    const { data } = await api.post("/invites", { allowed_modules: mods });
    setLink(`${window.location.origin}/login?invite=${data.token}`);
    toast.success("Convite gerado!");
    onCreated && onCreated();
  };
  const copy = () => { navigator.clipboard?.writeText(link); toast.success("Link copiado!"); };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setLink(""); }}>
      <DialogTrigger asChild>
        <Button className="gap-2" data-testid="invite-leader-button"><UserPlus className="h-4 w-4" /> Convidar líder</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Convidar líder (acesso liberado)</DialogTitle></DialogHeader>
        {link ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Compartilhe este link. Ao se cadastrar, o líder já entra aprovado com os módulos escolhidos.</p>
            <div className="flex gap-2">
              <Input value={link} readOnly className="text-xs" data-testid="invite-link-input" />
              <Button size="icon" onClick={copy} data-testid="copy-invite-button"><Copy className="h-4 w-4" /></Button>
            </div>
          </div>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">Selecione os módulos que o líder convidado poderá acessar:</p>
            <div className="py-2"><ModulePicker value={mods} onChange={setMods} /></div>
            <DialogFooter><Button onClick={create} className="gap-2" data-testid="generate-invite-button"><LinkIcon className="h-4 w-4" /> Gerar link de convite</Button></DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({ userId, userName, onDone, trigger }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("type"); // type | generate
  const [password, setPassword] = useState("");
  const [temp, setTemp] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = async () => {
    setBusy(true);
    try {
      const body = mode === "generate" ? { generate: true } : { password };
      const { data } = await api.post(`/users/${userId}/reset-password`, body);
      if (data.temporary_password) setTemp(data.temporary_password);
      else { toast.success("Senha redefinida"); setOpen(false); }
      onDone && onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) { setMode("type"); setPassword(""); setTemp(""); } }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent data-testid="reset-password-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5 text-primary" /> Redefinir senha — {userName}</DialogTitle></DialogHeader>
        {temp ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Senha temporária gerada. Copie e repasse à pessoa (peça para ela trocar depois no perfil):</p>
            <div className="flex gap-2">
              <Input value={temp} readOnly className="font-mono" data-testid="temp-password-value" />
              <Button size="icon" onClick={() => { navigator.clipboard?.writeText(temp); toast.success("Copiada!"); }} data-testid="copy-temp-password"><Copy className="h-4 w-4" /></Button>
            </div>
            <Button className="w-full" onClick={() => setOpen(false)} data-testid="close-reset-dialog">Fechar</Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex rounded-lg border border-border p-0.5">
              <Button variant={mode === "type" ? "secondary" : "ghost"} size="sm" className="flex-1 h-8" onClick={() => setMode("type")} data-testid="reset-mode-type">Digitar senha</Button>
              <Button variant={mode === "generate" ? "secondary" : "ghost"} size="sm" className="flex-1 h-8" onClick={() => setMode("generate")} data-testid="reset-mode-generate">Gerar temporária</Button>
            </div>
            {mode === "type" ? (
              <div>
                <Label>Nova senha (mín. 6 caracteres)</Label>
                <Input type="text" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5" minLength={6} data-testid="reset-password-input" placeholder="Digite a nova senha" />
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">O sistema vai gerar uma senha temporária segura para você copiar e repassar.</p>
            )}
            <DialogFooter>
              <Button onClick={reset} disabled={busy || (mode === "type" && password.trim().length < 6)} className="gap-2" data-testid="confirm-reset-password">
                <KeyRound className="h-4 w-4" /> {busy ? "Redefinindo…" : "Redefinir senha"}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function UsersAdmin() {
  const { user: me } = useAuth();
  const isApprover = APPROVER_ROLES.includes(me?.role);
  const [users, setUsers] = useState([]);
  const [pending, setPending] = useState([]);
  const [roles, setRoles] = useState([]);
  const [invites, setInvites] = useState([]);
  const [resetReqs, setResetReqs] = useState([]);
  const [progress, setProgress] = useState({ totals: { estudo: 0, escola: 0 }, users: {} });

  const load = useCallback(() => {
    api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/users/pending").then((r) => setPending(r.data)).catch(() => {});
    api.get("/roles").then((r) => setRoles(r.data)).catch(() => {});
    api.get("/invites").then((r) => setInvites(r.data)).catch(() => {});
    api.get("/learn/progress-summary").then((r) => setProgress(r.data)).catch(() => {});
    if (APPROVER_ROLES.includes(me?.role)) api.get("/password-reset-requests").then((r) => setResetReqs(r.data)).catch(() => {});
  }, [me]);

  useEffect(() => { load(); }, [load]);
  const roleLabel = (k) => roles.find((r) => r.key === k)?.label || ROLE_LABEL[k] || k;

  const remove = async (u) => {
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Removido");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao remover");
    }
  };

  const dismissReq = async (id) => {
    try { await api.post(`/password-reset-requests/${id}/dismiss`); toast.success("Solicitação dispensada"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const approved = users.filter((u) => u.status !== "pendente");

  return (
    <div className="space-y-8 animate-fade-up" data-testid="users-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight flex items-center gap-2">
            <ShieldCheck className="h-8 w-8 text-primary" /> Usuários & Acessos
          </h1>
          <p className="text-muted-foreground mt-1">Aprove líderes e defina quais módulos cada pessoa pode acessar.</p>
        </div>
        <div className="flex items-center gap-2">
          <RolesManager roles={roles} onChanged={load} />
          <CreateUserDialog roles={roles} onCreated={load} isApprover={isApprover} />
          <InviteDialog onCreated={load} />
        </div>
      </div>

      <Tabs defaultValue="pending">
        <TabsList>
          <TabsTrigger value="pending" data-testid="tab-pending">
            Aprovações {pending.length > 0 && <Badge className="ml-2 bg-accent text-accent-foreground">{pending.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-all-users">Todos os usuários</TabsTrigger>
          {isApprover && (
            <TabsTrigger value="resets" data-testid="tab-resets">
              Redefinições {resetReqs.length > 0 && <Badge className="ml-2 bg-accent text-accent-foreground">{resetReqs.length}</Badge>}
            </TabsTrigger>
          )}
          <TabsTrigger value="invites" data-testid="tab-invites">Convites</TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="mt-6">
          {pending.length === 0 ? (
            <Card className="p-12 text-center text-muted-foreground">
              <Clock className="h-10 w-10 mx-auto mb-3 opacity-50" /> Nenhum cadastro de líder aguardando aprovação.
            </Card>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {pending.map((u) => (
                <Card key={u.id} className="p-5" data-testid="pending-user-card">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold">{u.name}</span>
                    {(() => { const rc = roles.find((x) => x.key === u.role); return (
                      <Badge className="border-none text-white" style={{ backgroundColor: rc?.color || "#0EA5E9" }}>{roleLabel(u.role)}</Badge>
                    ); })()}
                  </div>
                  <div className="mt-2 text-sm text-muted-foreground space-y-0.5">
                    <div>{u.email}</div>
                    <div>{u.phone}</div>
                    <div>{u.address}</div>
                    {u.birth_date && <div>Nasc.: {new Date(u.birth_date + "T00:00:00").toLocaleDateString("pt-BR")}</div>}
                  </div>
                  <div className="mt-4 flex items-center gap-2">
                    {isApprover ? (
                      <>
                        <ApproveDialog user={u} onDone={load} />
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="sm" variant="ghost" className="gap-1.5 text-destructive" data-testid={`reject-user-${u.id}`}><Trash2 className="h-4 w-4" /> Recusar</Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Recusar {u.name}?</AlertDialogTitle>
                              <AlertDialogDescription>O cadastro será removido permanentemente.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction onClick={() => remove(u)}>Recusar</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </>
                    ) : (
                      <p className="text-xs text-muted-foreground" data-testid={`no-approve-perm-${u.id}`}>Somente Pastor Sênior e Coordenadores Gerais podem aprovar.</p>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="all" className="mt-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {approved.map((u) => (
              <Card key={u.id} className="p-5 flex flex-col" data-testid="user-card">
                <div className="flex items-center gap-2 flex-wrap">
                  <UserCog className="h-4 w-4 text-muted-foreground" />
                  <span className="font-semibold">{u.name}</span>
                  {(() => { const rc = roles.find((x) => x.key === u.role); return (
                    <Badge className="border-none text-white" style={{ backgroundColor: rc?.color || "#64748B" }}>{roleLabel(u.role)}</Badge>
                  ); })()}
                </div>
                <div className="mt-2 text-sm text-muted-foreground">{u.email}</div>
                <div className="mt-3 text-xs text-muted-foreground flex-1">
                  {u.is_admin ? "Acesso total" : `${(u.allowed_modules || []).length} módulos liberados`}
                </div>
                {!u.is_admin && (progress.totals.escola > 0 || progress.totals.estudo > 0) && (() => {
                  const p = progress.users[u.id] || { estudo: 0, escola: 0 };
                  return (
                    <div className="mt-1 flex flex-wrap gap-1.5" data-testid={`user-progress-${u.id}`}>
                      <Badge variant="secondary" className="text-[11px]">Escola: {p.escola}/{progress.totals.escola}</Badge>
                      <Badge variant="secondary" className="text-[11px]">Estudos: {p.estudo}/{progress.totals.estudo}</Badge>
                    </div>
                  );
                })()}
                {u.role !== "coordenador" && (
                  <div className="mt-4 space-y-2">
                    <RoleSelect user={u} roles={roles} onChanged={load} />
                    <div className="flex items-center gap-2">
                      <ModulesDialog user={u} onDone={load} />
                      {isApprover && (
                        <ResetPasswordDialog userId={u.id} userName={u.name} onDone={load}
                          trigger={<Button size="sm" variant="outline" className="gap-1.5" data-testid={`reset-password-${u.id}`}><KeyRound className="h-4 w-4" /> Senha</Button>} />
                      )}
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="ghost" className="gap-1.5 text-destructive" data-testid={`delete-user-${u.id}`}><Trash2 className="h-4 w-4" /></Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Remover {u.name}?</AlertDialogTitle>
                            <AlertDialogDescription>Esta conta será removida permanentemente.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction onClick={() => remove(u)}>Remover</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                )}
              </Card>
            ))}
          </div>
        </TabsContent>

        {isApprover && (
          <TabsContent value="resets" className="mt-6">
            {resetReqs.length === 0 ? (
              <Card className="p-12 text-center text-muted-foreground" data-testid="resets-empty">
                <KeyRound className="h-10 w-10 mx-auto mb-3 opacity-50" /> Nenhuma solicitação de redefinição de senha.
              </Card>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="resets-list">
                {resetReqs.map((req) => (
                  <Card key={req.id} className="p-5" data-testid={`reset-request-${req.id}`}>
                    <div className="font-semibold">{req.name || "—"}</div>
                    <div className="mt-1 text-sm text-muted-foreground space-y-0.5">
                      {req.email && <div>{req.email}</div>}
                      {req.phone && <div>{req.phone}</div>}
                      {req.identifier && <div className="text-xs">Informado: {req.identifier}</div>}
                    </div>
                    <div className="mt-4 flex items-center gap-2">
                      <ResetPasswordDialog userId={req.user_id} userName={req.name} onDone={load}
                        trigger={<Button size="sm" className="gap-1.5" data-testid={`resolve-reset-${req.id}`}><KeyRound className="h-4 w-4" /> Redefinir</Button>} />
                      <Button size="sm" variant="ghost" onClick={() => dismissReq(req.id)} data-testid={`dismiss-reset-${req.id}`}>Dispensar</Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        )}

        <TabsContent value="invites" className="mt-6">          {invites.length === 0 ? (
            <Card className="p-12 text-center text-muted-foreground">
              <LinkIcon className="h-10 w-10 mx-auto mb-3 opacity-50" /> Nenhum convite gerado ainda. Use "Convidar líder".
            </Card>
          ) : (
            <div className="space-y-2" data-testid="invites-list">
              {invites.map((i) => (
                <Card key={i.id} className="p-4 flex items-center justify-between gap-3" data-testid="invite-row">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Badge className={i.used ? "bg-secondary text-secondary-foreground border-none" : "bg-[hsl(var(--emerald))]/15 text-[hsl(var(--emerald))] border-none"}>
                        {i.used ? "Utilizado" : "Pendente"}
                      </Badge>
                      <span className="text-xs text-muted-foreground">{i.allowed_modules.length} módulos</span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 truncate font-mono">{`${window.location.origin}/login?invite=${i.token}`}</div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {!i.used && (
                      <Button variant="ghost" size="icon" onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/login?invite=${i.token}`); toast.success("Link copiado!"); }} data-testid={`copy-invite-${i.token}`}>
                        <Copy className="h-4 w-4" />
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" onClick={async () => { await api.delete(`/invites/${i.token}`); toast.success("Convite revogado"); load(); }} data-testid={`revoke-invite-${i.token}`}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
